import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import WorkoLogo from '../../../../assets/img/worko-symbol-ash.webp';
import { Aggregations, AggregationsSkillBucket } from '../../../../redux/ducks/prospect-search-result/types';
import { numberWithCommas } from '../../../../helpers/numbers';

export const MarketTable = ({ totalCount, aggregations }: { totalCount: number; aggregations: Aggregations }) => {
    const middleIndex = Math.ceil(aggregations.top_skills.buckets.length / 2); // Find the middle index
    const topSkills1: AggregationsSkillBucket[] = aggregations.top_skills.buckets.slice(0, middleIndex); // First half
    const topSkills2: AggregationsSkillBucket[] = aggregations.top_skills.buckets.slice(middleIndex); // Second half

    const marketEstSmallPercentage = (
        (aggregations.market_est.buckets.smallCompany.doc_count / aggregations.market_est.buckets['count'].doc_count) *
        100
    ).toFixed(1);

    const marketEstMediumPercentage = (
        (aggregations.market_est.buckets.mediumCompany.doc_count / aggregations.market_est.buckets['count'].doc_count) *
        100
    ).toFixed(1);

    const marketEstEnterprisePercentage = (
        (aggregations.market_est.buckets.enterpriseCompany.doc_count /
            aggregations.market_est.buckets['count'].doc_count) *
        100
    ).toFixed(1);

    return (
        <Box
            sx={{
                width: '100%',
                padding: '16px',
                display: 'flex',
                flexDirection: 'row',
                gap: '32px',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#FF6A76' }}>
                    {numberWithCommas(totalCount)}
                </Typography>

                <Typography
                    gutterBottom
                    sx={{ fontWeight: 'light', color: '#333', textAlign: 'center', fontSize: '1.125rem' }}
                >
                    Contacts found
                </Typography>
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                }}
            >
                {aggregations && aggregations.market_est && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        {/* Section Market est. by Company */}
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#333' }}>
                            Market est. by Company
                        </Typography>
                        <Table
                            size="small"
                            sx={{
                                borderRadius: 'var(--radius-xl, 14px)',
                                background: 'var(--grey-palette-grey-1, #F7F8FA)',
                            }}
                        >
                            <TableHead
                                sx={{
                                    padding: '4px var(--M-m-compact, 16px) var(--M-m-compact, 16px) 8px',
                                    gap: 'var(--S-s-regular, 8px)',
                                    alignSelf: 'stretch',
                                    borderRadius:
                                        'var(--radius-xl, 14px) var(--radius-xl, 14px) var(--radius-none, 0px) var(--radius-none, 0px)',
                                    background: '#3D2139',
                                }}
                            >
                                <TableRow
                                    sx={{
                                        padding: '4px var(--M-m-compact, 16px) var(--M-m-compact, 16px) 8px',
                                        borderRadius: 'var(--radius-xl, 14px)',

                                        '& > th:first-child': {
                                            borderRadius: '14px 0 0 0',
                                        },
                                        '& > th:last-child': {
                                            borderRadius: '0 14px 0 0',
                                        },
                                        '& th': {
                                            color: '#ffffff',
                                            fontSize: '14px',
                                            fontWeight: 400,
                                            padding: '6px 12px',
                                        },
                                    }}
                                >
                                    <TableCell sx={{ fontWeight: 'bold', color: '#555', textAlign: 'center' }}>
                                        Small
                                        <br />({aggregations.market_est.buckets.smallCompany.from}-
                                        {aggregations.market_est.buckets.smallCompany.to})
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', color: '#555', textAlign: 'center' }}>
                                        Med/Large
                                        <br />({aggregations.market_est.buckets.mediumCompany.from}-
                                        {aggregations.market_est.buckets.mediumCompany.to})
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', color: '#555', textAlign: 'center' }}>
                                        Enterprise
                                        <br />({aggregations.market_est.buckets.enterpriseCompany.from}+)
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody
                                sx={{
                                    boxShadow:
                                        '0px 1px 4px 0px rgba(20, 21, 26, 0.02), 0px 2px 7px 0px rgba(20, 21, 26, 0.07)',
                                    //border: '1px solid var(--grey-palette-grey-3, #E4E6EB)',
                                    borderRadius: 'var(--radius-xl, 14px)',
                                    '& > tr:first-child': {
                                        '& > td:first-child': {
                                            borderRadius: '14px 0 0 0',
                                        },
                                        '& > td:last-child': {
                                            borderRadius: '0 14px 0 0',
                                        },
                                    },
                                    '& > tr:last-child': {
                                        '& > td:first-child': {
                                            borderRadius: '0 0 0 14px',
                                        },
                                        '& > td:last-child': {
                                            borderRadius: '0 0 14px 0',
                                        },
                                    },
                                }}
                            >
                                <TableRow
                                    sx={{
                                        '& th, td': {
                                            background: 'white',
                                            borderTop: '1px solid var(--grey-palette-grey-3, #E4E6EB)',
                                            borderBottom: '1px solid var(--grey-palette-grey-3, #E4E6EB)',
                                            textAlign: 'center',
                                        },
                                        '&:last-child td, &:last-child th': { border: 0 },
                                        '& td': {
                                            color: '#3A4B59',
                                            fontSize: '14px',
                                            padding: '6px 12px',
                                        },
                                        '&:hover': {
                                            '& .add-button': {
                                                display: 'inline-flex',
                                            },
                                            '& th, td': {
                                                background: 'var(--grey-palette-grey-1, #F7F8FA)',
                                            },
                                        },
                                        cursor: 'pointer',
                                    }}
                                >
                                    <TableCell>{marketEstSmallPercentage}%</TableCell>
                                    <TableCell>{marketEstMediumPercentage}%</TableCell>
                                    <TableCell>{marketEstEnterprisePercentage}%</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </Box>
                )}

                {aggregations && aggregations.years_experience && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        {/* Section Market Tenure est. */}
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#333' }}>
                            Market Tenure est.
                        </Typography>
                        <Table
                            size="small"
                            sx={{
                                borderRadius: 'var(--radius-xl, 14px)',
                                background: 'var(--grey-palette-grey-1, #F7F8FA)',
                            }}
                        >
                            <TableHead
                                sx={{
                                    padding: '4px var(--M-m-compact, 16px) var(--M-m-compact, 16px) 8px',
                                    gap: 'var(--S-s-regular, 8px)',
                                    alignSelf: 'stretch',
                                    borderRadius:
                                        'var(--radius-xl, 14px) var(--radius-xl, 14px) var(--radius-none, 0px) var(--radius-none, 0px)',
                                    background: '#3D2139',
                                }}
                            >
                                <TableRow
                                    sx={{
                                        padding: '4px var(--M-m-compact, 16px) var(--M-m-compact, 16px) 8px',
                                        borderRadius: 'var(--radius-xl, 14px)',

                                        '& > th:first-child': {
                                            borderRadius: '14px 0 0 0',
                                        },
                                        '& > th:last-child': {
                                            borderRadius: '0 14px 0 0',
                                        },
                                        '& th': {
                                            color: '#ffffff',
                                            fontSize: '14px',
                                            fontWeight: 400,
                                            padding: '6px 12px',
                                            textAlign: 'center',
                                        },
                                    }}
                                >
                                    {aggregations.years_experience.buckets.map((bucket, index) => (
                                        <TableCell key={index} sx={{ fontWeight: 'bold', color: '#ffffff' }}>
                                            {bucket.to ? `${bucket.from}-${bucket.to} years` : `${bucket.from}+ years`}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody
                                sx={{
                                    boxShadow:
                                        '0px 1px 4px 0px rgba(20, 21, 26, 0.02), 0px 2px 7px 0px rgba(20, 21, 26, 0.07)',
                                    //border: '1px solid var(--grey-palette-grey-3, #E4E6EB)',
                                    borderRadius: 'var(--radius-xl, 14px)',
                                    '& > tr:first-child': {
                                        '& > td:first-child': {
                                            borderRadius: '14px 0 0 0',
                                        },
                                        '& > td:last-child': {
                                            borderRadius: '0 14px 0 0',
                                        },
                                    },
                                    '& > tr:last-child': {
                                        '& > td:first-child': {
                                            borderRadius: '0 0 0 14px',
                                        },
                                        '& > td:last-child': {
                                            borderRadius: '0 0 14px 0',
                                        },
                                    },
                                }}
                            >
                                <TableRow
                                    sx={{
                                        '& th, td': {
                                            background: 'white',
                                            borderTop: '1px solid var(--grey-palette-grey-3, #E4E6EB)',
                                            borderBottom: '1px solid var(--grey-palette-grey-3, #E4E6EB)',
                                            textAlign: 'center',
                                        },
                                        '&:last-child td, &:last-child th': { border: 0 },
                                        '& td': {
                                            color: '#3A4B59',
                                            fontSize: '14px',
                                            padding: '6px 12px',
                                        },
                                        '&:hover': {
                                            '& .add-button': {
                                                display: 'inline-flex',
                                            },
                                            '& th, td': {
                                                background: 'var(--grey-palette-grey-1, #F7F8FA)',
                                            },
                                        },
                                        cursor: 'pointer',
                                    }}
                                >
                                    {aggregations.years_experience.buckets.map((bucket, index) => (
                                        <TableCell>{numberWithCommas(bucket.doc_count)}</TableCell>
                                    ))}
                                </TableRow>
                            </TableBody>
                        </Table>
                    </Box>
                )}
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '16px',
                    flexGrow: 1,
                }}
            >
                {topSkills1 && (
                    <Box
                        sx={{
                            flexGrow: 1,
                        }}
                    >
                        {/* Section Top 10 Skills */}
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#333' }}>
                            Top 10 Skills
                        </Typography>
                        <Table
                            size="small"
                            sx={{
                                borderRadius: 'var(--radius-xl, 14px)',
                                background: 'var(--grey-palette-grey-1, #F7F8FA)',
                            }}
                        >
                            <TableHead
                                sx={{
                                    padding: '4px var(--M-m-compact, 16px) var(--M-m-compact, 16px) 8px',
                                    gap: 'var(--S-s-regular, 8px)',
                                    alignSelf: 'stretch',
                                    borderRadius:
                                        'var(--radius-xl, 14px) var(--radius-xl, 14px) var(--radius-none, 0px) var(--radius-none, 0px)',
                                    background: '#3D2139',
                                }}
                            >
                                <TableRow
                                    sx={{
                                        padding: '4px var(--M-m-compact, 16px) var(--M-m-compact, 16px) 8px',
                                        borderRadius: 'var(--radius-xl, 14px)',

                                        '& > th:first-child': {
                                            borderRadius: '14px 0 0 0',
                                        },
                                        '& > th:last-child': {
                                            borderRadius: '0 14px 0 0',
                                        },
                                        '& th': {
                                            color: '#ffffff',
                                            fontSize: '14px',
                                            fontWeight: 400,
                                            padding: '6px 12px',
                                        },
                                    }}
                                >
                                    <TableCell sx={{ fontWeight: 'bold', color: '#ffffff' }}>Skill</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', color: '#ffffff' }}>#</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody
                                sx={{
                                    boxShadow:
                                        '0px 1px 4px 0px rgba(20, 21, 26, 0.02), 0px 2px 7px 0px rgba(20, 21, 26, 0.07)',
                                    //border: '1px solid var(--grey-palette-grey-3, #E4E6EB)',
                                    borderRadius: 'var(--radius-xl, 14px)',
                                    '& > tr:first-child': {
                                        '& > td:first-child': {
                                            borderRadius: '14px 0 0 0',
                                        },
                                        '& > td:last-child': {
                                            borderRadius: '0 14px 0 0',
                                        },
                                    },
                                    '& > tr:last-child': {
                                        '& > td:first-child': {
                                            borderRadius: '0 0 0 14px',
                                        },
                                        '& > td:last-child': {
                                            borderRadius: '0 0 14px 0',
                                        },
                                    },
                                }}
                            >
                                {topSkills1.map((bucket, index) => (
                                    <TableRow
                                        sx={{
                                            '& th, td': {
                                                background: 'white',
                                                borderTop: '1px solid var(--grey-palette-grey-3, #E4E6EB)',
                                                borderBottom: '1px solid var(--grey-palette-grey-3, #E4E6EB)',
                                            },
                                            '&:last-child td, &:last-child th': { border: 0 },
                                            '& td': {
                                                color: '#3A4B59',
                                                fontSize: '14px',
                                                padding: '6px 12px',
                                            },
                                            '&:hover': {
                                                '& .add-button': {
                                                    display: 'inline-flex',
                                                },
                                                '& th, td': {
                                                    background: 'var(--grey-palette-grey-1, #F7F8FA)',
                                                },
                                            },
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <TableCell
                                            sx={{
                                                maxWidth: '200px',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                cursor: 'pointer',
                                            }}
                                            title="Business controlling"
                                        >
                                            {bucket.key}
                                        </TableCell>
                                        <TableCell>{numberWithCommas(bucket.doc_count)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                )}

                {topSkills2 && (
                    <Box
                        sx={{
                            flexGrow: 1,
                        }}
                    >
                        {/* Section Top 10 Skills */}
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#333' }}>
                            &nbsp;
                        </Typography>
                        <Table
                            size="small"
                            sx={{
                                borderRadius: 'var(--radius-xl, 14px)',
                                background: 'var(--grey-palette-grey-1, #F7F8FA)',
                            }}
                        >
                            <TableHead
                                sx={{
                                    padding: '4px var(--M-m-compact, 16px) var(--M-m-compact, 16px) 8px',
                                    gap: 'var(--S-s-regular, 8px)',
                                    alignSelf: 'stretch',
                                    borderRadius:
                                        'var(--radius-xl, 14px) var(--radius-xl, 14px) var(--radius-none, 0px) var(--radius-none, 0px)',
                                    background: '#3D2139',
                                }}
                            >
                                <TableRow
                                    sx={{
                                        padding: '4px var(--M-m-compact, 16px) var(--M-m-compact, 16px) 8px',
                                        borderRadius: 'var(--radius-xl, 14px)',

                                        '& > th:first-child': {
                                            borderRadius: '14px 0 0 0',
                                        },
                                        '& > th:last-child': {
                                            borderRadius: '0 14px 0 0',
                                        },
                                        '& th': {
                                            color: '#ffffff',
                                            fontSize: '14px',
                                            fontWeight: 400,
                                            padding: '6px 12px',
                                        },
                                    }}
                                >
                                    <TableCell sx={{ fontWeight: 'bold', color: '#ffffff' }}>Skill</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', color: '#ffffff' }}>#</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody
                                sx={{
                                    boxShadow:
                                        '0px 1px 4px 0px rgba(20, 21, 26, 0.02), 0px 2px 7px 0px rgba(20, 21, 26, 0.07)',
                                    //border: '1px solid var(--grey-palette-grey-3, #E4E6EB)',
                                    borderRadius: 'var(--radius-xl, 14px)',
                                    '& > tr:first-child': {
                                        '& > td:first-child': {
                                            borderRadius: '14px 0 0 0',
                                        },
                                        '& > td:last-child': {
                                            borderRadius: '0 14px 0 0',
                                        },
                                    },
                                    '& > tr:last-child': {
                                        '& > td:first-child': {
                                            borderRadius: '0 0 0 14px',
                                        },
                                        '& > td:last-child': {
                                            borderRadius: '0 0 14px 0',
                                        },
                                    },
                                }}
                            >
                                {topSkills2.map((bucket, index) => (
                                    <TableRow
                                        sx={{
                                            '& th, td': {
                                                background: 'white',
                                                borderTop: '1px solid var(--grey-palette-grey-3, #E4E6EB)',
                                                borderBottom: '1px solid var(--grey-palette-grey-3, #E4E6EB)',
                                            },
                                            '&:last-child td, &:last-child th': { border: 0 },
                                            '& td': {
                                                color: '#3A4B59',
                                                fontSize: '14px',
                                                padding: '6px 12px',
                                            },
                                            '&:hover': {
                                                '& .add-button': {
                                                    display: 'inline-flex',
                                                },
                                                '& th, td': {
                                                    background: 'var(--grey-palette-grey-1, #F7F8FA)',
                                                },
                                            },
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <TableCell
                                            sx={{
                                                maxWidth: '200px',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                cursor: 'pointer',
                                            }}
                                            title="Business controlling"
                                        >
                                            {bucket.key}
                                        </TableCell>
                                        <TableCell>{numberWithCommas(bucket.doc_count)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                )}
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '16px',
                    justifyContent: 'flex-end',
                    alignItems: 'flex-start',
                }}
            >
                <img alt="Worko logo" src={WorkoLogo} style={{ width: '5rem', height: '5rem' }} />
            </Box>
        </Box>
    );
};
